import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    GetXfLiveBoardMetrics,
    SearchXfParticipants,
    GetXfParticipant360,
    SimulateXfTraffic,
    ResetXfDemoTraffic,
    ResetXfAllData,
    ExportXfExcelReport,
    CreateXfScannerHubConnection,
    getOccupancySemaphore
} from '../../utilities/BL/XoloFest';
import { BoardDemoDrawer } from './BoardDemoDrawer';

const formatCurrencyMxn = (val) => {
    const num = Number(val || 0);
    return num.toLocaleString('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2
    });
};

const formatDateTimeShort = (utcStr) => {
    if (!utcStr) return 'Sin registro';
    try {
        const d = new Date(utcStr);
        if (isNaN(d.getTime())) return 'Sin registro';
        return d.toLocaleString('es-MX', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    } catch {
        return String(utcStr);
    }
};

const getTimelineIconConfig = (stationType = '', actionName = '') => {
    const upperType = String(stationType || '').toUpperCase();
    const upperAction = String(actionName || '').toUpperCase();

    if (upperAction.includes('CANJE') || upperAction.includes('CUPÓN') || upperAction.includes('REWARD')) {
        return {
            icon: 'bi-gift-fill',
            color: '#f59e0b',
            bg: 'rgba(245, 158, 11, 0.18)',
            badge: 'CANJE RECOMPENSA'
        };
    }
    if (upperType === 'ACCESS') {
        return {
            icon: 'bi-door-open-fill',
            color: '#10b981',
            bg: 'rgba(16, 185, 129, 0.18)',
            badge: 'ACCESO PUERTA / ZONA'
        };
    }
    if (upperType === 'POS_STORE') {
        return {
            icon: 'bi-bag-check-fill',
            color: '#fbbf24',
            bg: 'rgba(251, 191, 36, 0.18)',
            badge: 'CONSUMO POS'
        };
    }
    if (upperType === 'ACTIVATION') {
        return {
            icon: 'bi-trophy-fill',
            color: '#dc2626',
            bg: 'rgba(220, 38, 38, 0.2)',
            badge: 'ACTIVACIÓN PATROCINADOR'
        };
    }
    return {
        icon: 'bi-patch-check-fill',
        color: '#a1a1aa',
        bg: 'rgba(255, 255, 255, 0.08)',
        badge: 'INTERACCIÓN'
    };
};

export const XoloFestBoard = () => {
    const navigate = useNavigate();

    // Estado de métricas globales en vivo
    const [metrics, setMetrics] = useState(null);
    const [loadingMetrics, setLoadingMetrics] = useState(true);
    const [simulating, setSimulating] = useState(false);
    const [signalrConnected, setSignalrConnected] = useState(false);
    const [lastUpdatedTime, setLastUpdatedTime] = useState(() => new Date());
    const [liveToast, setLiveToast] = useState(null);
    const [showTopDemoBar, setShowTopDemoBar] = useState(true);
    const [demoDrawerOpen, setDemoDrawerOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('ALL'); // 'ALL' | 'OCCUPANCY' | 'COMMERCIAL' | 'SPONSORS' | 'SUPPORT'
    const [showAuditLogs, setShowAuditLogs] = useState(false); // Divulgación progresiva de bitácora secundaria

    // Estado del Inspector 360° del Aficionado
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedParticipant360, setSelectedParticipant360] = useState(null);
    const [loading360, setLoading360] = useState(false);
    const [revealPii, setRevealPii] = useState(false);

    // Estado de exportación ejecutiva Excel (.xlsx) (HU-07)
    const [exportingExcel, setExportingExcel] = useState(false);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);

    // Estado del modal de seguridad para reiniciar Xolo Fest
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [resetPasswordInput, setResetPasswordInput] = useState('');
    const [resetPasswordError, setResetPasswordError] = useState('');
    const [resettingData, setResettingData] = useState(false);

    const triggerToast = useCallback((title, detail, tone = 'gold') => {
        setLiveToast({ id: Date.now(), title, detail, tone });
    }, []);

    useEffect(() => {
        if (!liveToast) return undefined;
        const timer = setTimeout(() => setLiveToast(null), 4800);
        return () => clearTimeout(timer);
    }, [liveToast]);

    // Cargar métricas del Centro de Mando
    const fetchLiveMetrics = useCallback(async (silent = false) => {
        if (!silent) setLoadingMetrics(true);
        const res = await GetXfLiveBoardMetrics();
        if (!silent) setLoadingMetrics(false);

        if (res.success) {
            setMetrics(res.data || res);
            setLastUpdatedTime(new Date());
        }
    }, []);

    // Buscar participantes para el Inspector 360°
    const runParticipantSearch = useCallback(async (q = '') => {
        setSearching(true);
        const res = await SearchXfParticipants(q);
        setSearching(false);
        if (res.success && Array.isArray(res.items)) {
            setSearchResults(res.items);
        }
    }, []);

    useEffect(() => {
        fetchLiveMetrics(false);
        runParticipantSearch('');
    }, [fetchLiveMetrics, runParticipantSearch]);

    // Debounce de búsqueda al escribir en el Inspector 360°
    useEffect(() => {
        const timer = setTimeout(() => {
            runParticipantSearch(searchQuery);
        }, 280);
        return () => clearTimeout(timer);
    }, [searchQuery, runParticipantSearch]);

    // Conexión SignalR en tiempo real (/hub/xf?role=board)
    useEffect(() => {
        const hubInstance = CreateXfScannerHubConnection({
            onConnectionStateChanged: (connected) => {
                setSignalrConnected(Boolean(connected));
            },
            onZoneOccupancyUpdated: (payload) => {
                if (payload && payload.zoneCode) {
                    setMetrics((prev) => {
                        if (!prev || !Array.isArray(prev.zones)) return prev;
                        const updatedZones = prev.zones.map((z) =>
                            z.zoneCode === payload.zoneCode
                                ? {
                                      ...z,
                                      currentOccupancy:
                                          payload.currentOccupancy ?? z.currentOccupancy,
                                      capacityLimit: payload.capacityLimit ?? z.capacityLimit,
                                      occupancyPercentage:
                                          payload.occupancyPercentage ?? z.occupancyPercentage,
                                      semaphoreStatus:
                                          payload.semaphoreStatus ?? z.semaphoreStatus
                                  }
                                : z
                        );
                        return { ...prev, zones: updatedZones };
                    });
                }
                fetchLiveMetrics(true);
            },
            onStampAdded: (payload) => {
                triggerToast(
                    `📡 Pulso en Vivo: ${payload?.stationName || payload?.stationCode || 'Estación'}`,
                    `${payload?.ticketFolio || 'Aficionado'} • ${payload?.actionName || 'Sello registrado'}`,
                    'gold'
                );
                fetchLiveMetrics(true);
                runParticipantSearch(searchQuery);
            },
            onRewardUnlocked: (payload) => {
                triggerToast(
                    '🏆 Recompensa Desbloqueada en Vivo',
                    `Folio ${payload?.ticketFolio || ''} ha completado el circuito Xolo Fest.`,
                    'green'
                );
                fetchLiveMetrics(true);
            },
            onConfigUpdated: () => {
                fetchLiveMetrics(true);
            },
            onParticipantReset: () => {
                fetchLiveMetrics(true);
                runParticipantSearch(searchQuery);
            }
        });

        return () => {
            hubInstance.stop();
        };
    }, [fetchLiveMetrics, runParticipantSearch, searchQuery, triggerToast]);

    // Abrir modal de Expediente 360°
    const handleOpenParticipant360 = async (ticketFolio) => {
        if (!ticketFolio) return;
        setRevealPii(false);
        setLoading360(true);
        const res = await GetXfParticipant360(ticketFolio);
        setLoading360(false);

        if (res.success && res.data) {
            setSelectedParticipant360(res.data);
        } else {
            triggerToast(
                '⚠️ No se pudo abrir el expediente',
                res.message || 'Verifica el folio del participante.',
                'red'
            );
        }
    };

    // Acciones del Simulador Demo Centro de Mando (HU-06)
    const handleSimulateBurst = async (payload, label) => {
        if (simulating) return;
        setSimulating(true);
        const res = await SimulateXfTraffic(payload);
        setSimulating(false);

        if (res.success) {
            if (res.metrics) {
                setMetrics(res.metrics);
                setLastUpdatedTime(new Date());
            } else {
                await fetchLiveMetrics(true);
            }
            await runParticipantSearch(searchQuery);
            triggerToast(
                `⚡ ${label}`,
                res.message || 'Telemetría y aforos actualizados en tiempo real.',
                'gold'
            );
        } else {
            triggerToast(
                '⚠️ Aviso del Simulador',
                res.message || 'No fue posible simular la ráfaga de tráfico.',
                'red'
            );
        }
    };

    const handleResetDemoBoard = async () => {
        if (simulating) return;
        setSimulating(true);
        const res = await ResetXfDemoTraffic();
        await fetchLiveMetrics(true);
        await runParticipantSearch('');
        setSimulating(false);

        if (res.success) {
            triggerToast(
                '🧹 Tráfico Demo Reiniciado',
                'Los contadores de aforo, ventas y activaciones han vuelto a su estado semilla.',
                'green'
            );
        }
    };

    // Flujo de reinicio total de Xolo Fest con confirmación de contraseña maestra
    const handleOpenResetModal = () => {
        setResetPasswordInput('');
        setResetPasswordError('');
        setIsResetModalOpen(true);
    };

    const handleCloseResetModal = () => {
        if (resettingData) return;
        setIsResetModalOpen(false);
        setResetPasswordInput('');
        setResetPasswordError('');
    };

    const handleConfirmResetSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!resetPasswordInput) {
            setResetPasswordError('Ingresa la contraseña de autorización.');
            return;
        }

        setResettingData(true);
        setResetPasswordError('');
        const res = await ResetXfAllData(resetPasswordInput);
        setResettingData(false);

        if (res.success) {
            setIsResetModalOpen(false);
            setResetPasswordInput('');
            setResetPasswordError('');
            await fetchLiveMetrics(false);
            await runParticipantSearch('');
            triggerToast(
                '🧹 Xolo Fest Reiniciado',
                'Toda la información operativa (participantes, compras, activaciones y aforos) ha sido borrada. Ya puedes comenzar una prueba desde cero.',
                'green'
            );
        } else {
            setResetPasswordError(res.message || 'Contraseña incorrecta o error al reiniciar.');
        }
    };

    // Exportación del Reporte Ejecutivo Multi-Hoja en formato Excel (.xlsx) (HU-07)
    const handleExportExcel = async (maskPii = false) => {
        if (exportingExcel) return;
        setExportMenuOpen(false);
        setExportingExcel(true);
        const res = await ExportXfExcelReport(maskPii);
        setExportingExcel(false);

        if (res.success) {
            triggerToast(
                maskPii
                    ? '📊 Reporte Ejecutivo Descargado (PII Protegido)'
                    : '📊 Reporte Completo de Auditoría Descargado (PII Visible)',
                `Archivo generado: ${res.fileName} (4 hojas nativas: Resumen Ejecutivo, Base de Participantes, Ventas POS y Activaciones).`,
                'green'
            );
        } else {
            triggerToast(
                '⚠️ Error al exportar Reporte Excel (.xlsx)',
                res.message || 'Verifica la conexión con el servidor e intenta nuevamente.',
                'red'
            );
        }
    };

    // Extracción segura de KPIs
    const totalRegistered = Number(metrics?.totalRegisteredTickets || 0);
    const totalAttended = Number(metrics?.totalAttendedParticipants || 0);
    const attendancePct = Number(
        metrics?.attendancePercentage ??
            (totalRegistered > 0 ? ((totalAttended / totalRegistered) * 100).toFixed(1) : 0)
    );

    const globalOccupancy = Number(metrics?.globalCurrentOccupancy || 0);
    const globalCapacity = Number(metrics?.globalCapacityLimit || 5800);
    const globalOccupancyPct = Number(
        metrics?.globalOccupancyPercentage ??
            (globalCapacity > 0 ? ((globalOccupancy / globalCapacity) * 100).toFixed(1) : 0)
    );

    const commerce = metrics?.commerce || {};
    const totalRevenue = Number(commerce.totalRevenue || 0);
    const totalTransactions = Number(commerce.totalTransactionsCount || 0);
    const averageTicket = Number(commerce.averageTicket || 0);
    const topProducts = Array.isArray(commerce.topProducts) ? commerce.topProducts : [];

    const souvenirs = metrics?.souvenirs || {};
    const physicalLimit = Number(souvenirs.totalPhysicalStockLimit || 300);
    const physicalRedeemed = Number(souvenirs.totalPhysicalRedeemed || 0);
    const physicalRemaining = Number(
        souvenirs.physicalStockRemaining ?? Math.max(0, physicalLimit - physicalRedeemed)
    );
    const digitalBadgesAwarded = Number(souvenirs.totalDigitalBadgesAwarded || 0);
    const stockRedeemedPct = Number(
        souvenirs.stockRedeemedPercentage ??
            (physicalLimit > 0 ? ((physicalRedeemed / physicalLimit) * 100).toFixed(1) : 0)
    );

    const sponsors = metrics?.sponsors || {};
    const totalActivations = Number(sponsors.totalActivationsCount || 0);
    const brandRankings = Array.isArray(sponsors.brandRankings) ? sponsors.brandRankings : [];

    const zones = Array.isArray(metrics?.zones) ? metrics.zones : [];
    const recentLogs = Array.isArray(metrics?.recentAccessLogs) ? metrics.recentAccessLogs : [];

    return (
        <div
            style={{
                minHeight: '100vh',
                background: "url('/img/page/backgrounds/Fondo-SelfieFan-Femenil.jpeg') center top / cover no-repeat fixed",
                color: '#f4f4f5',
                fontFamily: "'Montserrat', 'Inter', system-ui, sans-serif",
                paddingBottom: '4rem'
            }}
        >
            {/* BARRA SUPERIOR EJECUTIVA + SIMULADOR DEMO CENTRO DE MANDO (HU-06) */}
            {showTopDemoBar ? (
                <div
                    style={{
                        background: 'rgba(12, 12, 16, 0.96)',
                        borderBottom: '1px solid rgba(245, 158, 11, 0.35)',
                        backdropFilter: 'blur(12px)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1030
                    }}
                >
                    <div className="container-fluid px-3 px-lg-4" style={{ maxWidth: '1440px' }}>
                        <div className="d-flex align-items-center justify-content-between py-2 flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span
                                    className="badge rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2"
                                    style={{
                                        background: signalrConnected
                                            ? 'rgba(16, 185, 129, 0.2)'
                                            : 'rgba(245, 158, 11, 0.18)',
                                        border: signalrConnected
                                            ? '1px solid #10b981'
                                            : '1px solid #f59e0b',
                                        color: '#ffffff',
                                        fontSize: '0.74rem',
                                        fontWeight: 800,
                                        letterSpacing: '0.6px'
                                    }}
                                >
                                    <span
                                        style={{
                                            width: '9px',
                                            height: '9px',
                                            borderRadius: '50%',
                                            background: signalrConnected ? '#10b981' : '#f59e0b',
                                            boxShadow: signalrConnected
                                                ? '0 0 10px #10b981'
                                                : '0 0 8px #f59e0b',
                                            display: 'inline-block'
                                        }}
                                    />
                                    {signalrConnected
                                        ? '🟢 En Vivo'
                                        : '🟡 Sincronizando en vivo...'}
                                </span>

                            <span
                                className="text-secondary d-none d-md-inline"
                                style={{ fontSize: '0.75rem' }}
                            >
                                Última sincronización:{' '}
                                <strong className="text-light">
                                    {lastUpdatedTime.toLocaleTimeString('es-MX')}
                                </strong>
                            </span>
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <button
                                type="button"
                                onClick={() => navigate('/demo/xolofest')}
                                className="btn btn-sm"
                                style={{
                                    background: 'rgba(255,255,255,0.06)',
                                    border: '1px solid rgba(255,255,255,0.15)',
                                    color: '#e4e4e7',
                                    fontSize: '0.74rem',
                                    fontWeight: 700
                                }}
                            >
                                <i className="bi bi-ticket-perforated text-warning me-1"></i>
                                Pre-Registro
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/demo/xolofest/passport')}
                                className="btn btn-sm"
                                style={{
                                    background: 'rgba(255,255,255,0.06)',
                                    border: '1px solid rgba(255,255,255,0.15)',
                                    color: '#e4e4e7',
                                    fontSize: '0.74rem',
                                    fontWeight: 700
                                }}
                            >
                                <i className="bi bi-passport text-warning me-1"></i>
                                Pasaporte
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/demo/xolofest/scanner')}
                                className="btn btn-sm"
                                style={{
                                    background: 'rgba(255,255,255,0.06)',
                                    border: '1px solid rgba(255,255,255,0.15)',
                                    color: '#e4e4e7',
                                    fontSize: '0.74rem',
                                    fontWeight: 700
                                }}
                            >
                                <i className="bi bi-qr-code-scan text-danger me-1"></i>
                                Scanner Staff
                            </button>

                            {/* MENÚ DESPLEGABLE HU-07: EXPORTAR REPORTE EXCEL (.XLSX) */}
                            <div className="position-relative">
                                <button
                                    type="button"
                                    disabled={exportingExcel}
                                    onClick={() => setExportMenuOpen((prev) => !prev)}
                                    className="btn btn-sm d-inline-flex align-items-center gap-2"
                                    style={{
                                        background:
                                            'linear-gradient(135deg, rgba(16, 185, 129, 0.28) 0%, rgba(5, 150, 105, 0.45) 100%)',
                                        border: '1.5px solid #10b981',
                                        color: '#ecfdf5',
                                        fontSize: '0.76rem',
                                        fontWeight: 800,
                                        boxShadow: '0 4px 14px rgba(16, 185, 129, 0.25)'
                                    }}
                                >
                                    {exportingExcel ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm text-light"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                            <span>Generando Excel (.xlsx)...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>📊 Exportar Reporte Excel (.xlsx)</span>
                                            <span>{exportMenuOpen ? '▲' : '▼'}</span>
                                        </>
                                    )}
                                </button>

                                <AnimatePresence>
                                    {exportMenuOpen && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -6, scale: 0.98 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: -6, scale: 0.98 }}
                                            className="position-absolute end-0 mt-2 p-2 rounded-3"
                                            style={{
                                                minWidth: '320px',
                                                background: '#141418',
                                                border: '1.5px solid #10b981',
                                                boxShadow: '0 18px 40px rgba(0, 0, 0, 0.85)',
                                                zIndex: 1080
                                            }}
                                        >
                                            <div
                                                className="px-2 py-1 mb-1 text-uppercase fw-bold"
                                                style={{
                                                    fontSize: '0.66rem',
                                                    color: '#6ee7b7',
                                                    letterSpacing: '0.7px'
                                                }}
                                            >
                                                Libro Nativo Multi-Hoja (4 Pestañas .xlsx)
                                            </div>

                                            <button
                                                type="button"
                                                disabled={exportingExcel}
                                                onClick={() => handleExportExcel(true)}
                                                className="btn btn-sm w-100 text-start p-2 mb-1 rounded-2"
                                                style={{
                                                    background: 'rgba(16, 185, 129, 0.16)',
                                                    border: '1px solid rgba(16, 185, 129, 0.45)',
                                                    color: '#ffffff'
                                                }}
                                            >
                                                <div
                                                    className="fw-bold"
                                                    style={{ fontSize: '0.78rem', color: '#a7f3d0' }}
                                                >
                                                    📥 Reporte Ejecutivo (PII Protegido)
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: '0.68rem',
                                                        color: '#d4d4d8'
                                                    }}
                                                >
                                                    Enmascara teléfonos y correos (maskPii=true) para compartir con patrocinadores.
                                                </div>
                                            </button>

                                            <button
                                                type="button"
                                                disabled={exportingExcel}
                                                onClick={() => handleExportExcel(false)}
                                                className="btn btn-sm w-100 text-start p-2 rounded-2"
                                                style={{
                                                    background: 'rgba(245, 158, 11, 0.14)',
                                                    border: '1px solid rgba(245, 158, 11, 0.45)',
                                                    color: '#ffffff'
                                                }}
                                            >
                                                <div
                                                    className="fw-bold"
                                                    style={{ fontSize: '0.78rem', color: '#fde68a' }}
                                                >
                                                    📥 Reporte Completo de Auditoría (PII Visible)
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: '0.68rem',
                                                        color: '#d4d4d8'
                                                    }}
                                                >
                                                    Incluye datos completos de contacto y auditoría financiera (maskPii=false).
                                                </div>
                                            </button>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            <button
                                type="button"
                                onClick={() => setDemoDrawerOpen(true)}
                                className="btn btn-sm d-inline-flex align-items-center gap-2"
                                style={{
                                    background: 'rgba(245, 158, 11, 0.18)',
                                    border: '1px solid #f59e0b',
                                    color: '#fde68a',
                                    fontSize: '0.75rem',
                                    fontWeight: 800
                                }}
                            >
                                <span>🛠️ Simulador Demo (HU-06)</span>
                                <span
                                    className="badge rounded-pill bg-warning text-dark px-1"
                                    style={{ fontSize: '0.64rem', fontWeight: 900 }}
                                >
                                    Abrir
                                </span>
                            </button>

                            <button
                                type="button"
                                onClick={handleOpenResetModal}
                                className="btn btn-sm d-inline-flex align-items-center gap-1"
                                style={{
                                    background: 'rgba(220, 38, 38, 0.2)',
                                    border: '1px solid rgba(220, 38, 38, 0.65)',
                                    color: '#fca5a5',
                                    fontSize: '0.74rem',
                                    fontWeight: 800
                                }}
                                title="Borrar y reiniciar toda la información de Xolo Fest para iniciar prueba desde cero"
                            >
                                <i className="bi bi-trash3-fill text-danger me-1"></i>
                                <span>Reiniciar Xolo Fest</span>
                            </button>

                            <button
                                type="button"
                                onClick={() => setShowTopDemoBar(false)}
                                className="btn btn-sm d-inline-flex align-items-center gap-1"
                                style={{
                                    background: 'rgba(255,255,255,0.06)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    color: '#a1a1aa',
                                    fontSize: '0.74rem'
                                }}
                                title="Ocultar menú superior del demo"
                            >
                                <i className="bi bi-eye-slash"></i>
                                <span className="d-none d-sm-inline">Ocultar Menú</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setShowTopDemoBar(true)}
                    className="btn btn-sm rounded-pill position-fixed top-0 end-0 m-3 px-3 py-2 shadow-lg fw-bold d-inline-flex align-items-center gap-2"
                    style={{
                        zIndex: 1040,
                        background: 'rgba(24, 24, 27, 0.94)',
                        border: '1px solid #f59e0b',
                        color: '#fde68a',
                        fontSize: '0.76rem',
                        backdropFilter: 'blur(8px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.6)'
                    }}
                    title="Mostrar menú superior del demo"
                >
                    <i className="bi bi-tools text-warning"></i>
                    <span>Menú Demo</span>
                </button>
            )}

            {/* CONTENEDOR PRINCIPAL DEL CENTRO DE MANDO */}
            <div className="container-fluid px-3 px-lg-4 pt-4" style={{ maxWidth: '1440px' }}>
                {/* Toast de Eventos SignalR en Vivo */}
                <AnimatePresence>
                    {liveToast && (
                        <motion.div
                            key={liveToast.id}
                            initial={{ opacity: 0, y: -15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            className="p-3 rounded-4 mb-3 d-flex align-items-center justify-content-between"
                            style={{
                                background:
                                    liveToast.tone === 'red'
                                        ? 'rgba(153, 27, 27, 0.95)'
                                        : liveToast.tone === 'green'
                                        ? 'rgba(6, 95, 70, 0.95)'
                                        : 'rgba(24, 24, 27, 0.96)',
                                border: '1px solid #f59e0b',
                                boxShadow: '0 12px 30px rgba(0,0,0,0.75)'
                            }}
                        >
                            <div>
                                <div className="fw-bold text-white" style={{ fontSize: '0.9rem' }}>
                                    {liveToast.title}
                                </div>
                                <div style={{ color: '#fde68a', fontSize: '0.78rem' }}>
                                    {liveToast.detail}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setLiveToast(null)}
                                className="btn btn-sm text-white border-0"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* CABECERA EJECUTIVA */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
                    <div>
                        <div className="d-inline-flex align-items-center gap-2 mb-1">
                            <span
                                className="badge rounded-pill px-3 py-1"
                                style={{
                                    background: 'rgba(220, 38, 38, 0.22)',
                                    border: '1px solid #dc2626',
                                    color: '#fca5a5',
                                    fontSize: '0.7rem',
                                    fontWeight: 800,
                                    letterSpacing: '1px'
                                }}
                            >
                                CLUB TIJUANA XOLOITZCUINTLES • CENTRO DE MANDO EJECUTIVO
                            </span>
                        </div>
                        <h1
                            className="fw-black text-uppercase mb-0"
                            style={{
                                fontWeight: 900,
                                fontSize: 'clamp(1.45rem, 3vw, 2.1rem)',
                                letterSpacing: '-0.5px',
                                color: '#ffffff'
                            }}
                        >
                            XOLO FEST 2026 — LIVE BOARD & INSPECTOR 360°
                        </h1>
                    </div>

                    <div
                        className="px-3 py-2 rounded-3 d-flex align-items-center gap-2"
                        style={{
                            background: 'rgba(24, 24, 27, 0.9)',
                            border: '1px solid rgba(245, 158, 11, 0.4)'
                        }}
                    >
                        <i className="bi bi-ticket-detailed-fill text-warning fs-5"></i>
                        <div style={{ fontSize: '0.76rem' }}>
                            <div className="fw-bold text-white">
                                🎰 Tómbola Perrona en Escenario Físico
                            </div>
                            <span style={{ color: '#a1a1aa' }}>
                                Folios asignados en pre-registro:{' '}
                                <strong style={{ color: '#fde68a' }}>{totalRegistered}</strong>{' '}
                                boletos activos
                            </span>
                        </div>
                    </div>
                </div>

                {/* =========================================================================
                    NAVEGACIÓN POR TILES / VISTAS DE ROL EJECUTIVO
                   ========================================================================= */}
                <div
                    className="d-flex align-items-center justify-content-between p-2 rounded-4 mb-4 flex-wrap gap-2"
                    style={{
                        background: 'rgba(24, 24, 27, 0.95)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
                    }}
                >
                    <div
                        className="d-flex align-items-center gap-2 overflow-x-auto pb-1 flex-nowrap"
                        style={{
                            scrollbarWidth: 'none',
                            WebkitOverflowScrolling: 'touch'
                        }}
                    >
                        {[
                            {
                                id: 'ALL',
                                label: 'Vista General (Todo)',
                                icon: 'bi-grid-fill',
                                badge: 'Completo'
                            },
                            {
                                id: 'OCCUPANCY',
                                label: 'Aforo y Zonas',
                                icon: 'bi-shield-fill-check',
                                badge: `${globalOccupancyPct}% Aforo`
                            },
                            {
                                id: 'COMMERCIAL',
                                label: 'Ventas POS',
                                icon: 'bi-bag-check-fill',
                                badge: formatCurrencyMxn(totalRevenue)
                            },
                            {
                                id: 'SPONSORS',
                                label: 'Patrocinadores y Premios',
                                icon: 'bi-trophy-fill',
                                badge: `${totalActivations} retos`
                            },
                            {
                                id: 'SUPPORT',
                                label: 'Inspector 360°',
                                icon: 'bi-person-bounding-box',
                                badge: `${searchResults.length} aficionados`
                            }
                        ].map((tab) => {
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setActiveTab(tab.id)}
                                    className="btn btn-sm d-inline-flex align-items-center gap-2 px-3 py-2 rounded-3 transition-all flex-shrink-0"
                                    style={{
                                        background: isActive
                                            ? '#dc2626'
                                            : 'rgba(255, 255, 255, 0.04)',
                                        border: isActive
                                            ? '1.5px solid #ef4444'
                                            : '1px solid rgba(255, 255, 255, 0.08)',
                                        color: isActive ? '#ffffff' : '#a1a1aa',
                                        fontWeight: isActive ? 800 : 600,
                                        fontSize: '0.8rem',
                                        boxShadow: isActive ? '0 4px 14px rgba(220, 38, 38, 0.4)' : 'none'
                                    }}
                                >
                                    <i
                                        className={`bi ${tab.icon}`}
                                        style={{ color: isActive ? '#ffffff' : '#71717a' }}
                                    ></i>
                                    <span>{tab.label}</span>
                                    <span
                                        className="badge rounded-pill"
                                        style={{
                                            background: isActive
                                                ? 'rgba(0, 0, 0, 0.3)'
                                                : 'rgba(255, 255, 255, 0.06)',
                                            color: isActive ? '#ffffff' : '#a1a1aa',
                                            fontSize: '0.68rem',
                                            fontWeight: 700
                                        }}
                                    >
                                        {tab.badge}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {activeTab !== 'ALL' && (
                        <button
                            type="button"
                            onClick={() => setActiveTab('ALL')}
                            className="btn btn-sm text-secondary d-inline-flex align-items-center gap-1"
                            style={{ fontSize: '0.74rem' }}
                        >
                            <i className="bi bi-arrow-counterclockwise"></i>
                            <span>Restablecer a Vista General</span>
                        </button>
                    )}
                </div>

                {/* =========================================================================
                    SECCIÓN 1: 4 KPIS MAESTROS INTERACTIVOS (TILES HORIZONTALES CON SWIPE)
                   ========================================================================= */}
                <div
                    className="d-flex overflow-x-auto pb-2 gap-3 mb-4"
                    style={{
                        scrollSnapType: 'x mandatory',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'thin'
                    }}
                >
                    {/* KPI 1: Asistencia Presencial (Tile interactivo -> SUPPORT) */}
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                            setActiveTab((prev) => (prev === 'SUPPORT' ? 'ALL' : 'SUPPORT'))
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                setActiveTab((prev) => (prev === 'SUPPORT' ? 'ALL' : 'SUPPORT'));
                            }
                        }}
                        className="rounded-4 p-3 d-flex flex-column justify-content-between transition-all flex-shrink-0"
                        style={{
                            flex: '1 0 clamp(250px, 75vw, 300px)',
                            minWidth: '240px',
                            scrollSnapAlign: 'start',
                            background:
                                activeTab === 'SUPPORT'
                                    ? 'linear-gradient(180deg, #1f1d18 0%, #18181b 100%)'
                                    : '#18181b',
                            border:
                                activeTab === 'SUPPORT'
                                    ? '2px solid #10b981'
                                    : '1px solid rgba(255, 255, 255, 0.08)',
                            boxShadow:
                                activeTab === 'SUPPORT'
                                    ? '0 0 20px rgba(16, 185, 129, 0.35)'
                                    : '0 4px 12px rgba(0,0,0,0.35)',
                            cursor: 'pointer'
                        }}
                    >
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span
                                    className="text-uppercase fw-bold"
                                    style={{
                                        color: activeTab === 'SUPPORT' ? '#ffffff' : '#a1a1aa',
                                        fontSize: '0.72rem',
                                        letterSpacing: '0.8px'
                                    }}
                                >
                                    👥 Asistencia Presencial
                                </span>
                                <span
                                    className="badge rounded-pill"
                                    style={{
                                        background:
                                            activeTab === 'SUPPORT'
                                                ? '#10b981'
                                                : 'rgba(255, 255, 255, 0.08)',
                                        color: activeTab === 'SUPPORT' ? '#09090b' : '#d4d4d8',
                                        fontWeight: 800,
                                        fontSize: '0.7rem'
                                    }}
                                >
                                    {activeTab === 'SUPPORT' ? '✓ Activo' : `${attendancePct}%`}
                                </span>
                            </div>
                            <div
                                className="fw-black text-white mb-1"
                                style={{ fontSize: '1.85rem', fontWeight: 900 }}
                            >
                                {totalAttended}{' '}
                                <span style={{ fontSize: '1rem', color: '#71717a' }}>
                                    / {totalRegistered}
                                </span>
                            </div>
                        </div>

                        <div>
                            <div
                                className="progress rounded-pill mb-2"
                                style={{ height: '7px', background: '#09090b' }}
                            >
                                <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{
                                        width: `${Math.min(100, attendancePct)}%`,
                                        background:
                                            activeTab === 'SUPPORT'
                                                ? '#10b981'
                                                : 'linear-gradient(90deg, #52525b 0%, #71717a 100%)'
                                    }}
                                />
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <small style={{ color: '#71717a', fontSize: '0.7rem' }}>
                                    Check-In confirmado
                                </small>
                                <small
                                    style={{
                                        color: activeTab === 'SUPPORT' ? '#6ee7b7' : '#a1a1aa',
                                        fontSize: '0.68rem',
                                        fontWeight: 700
                                    }}
                                >
                                    {activeTab === 'SUPPORT' ? 'Mostrando afición ↓' : 'Filtrar afición →'}
                                </small>
                            </div>
                        </div>
                    </div>

                    {/* KPI 2: Capacidad Global (Tile interactivo -> OCCUPANCY) */}
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                            setActiveTab((prev) => (prev === 'OCCUPANCY' ? 'ALL' : 'OCCUPANCY'))
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                setActiveTab((prev) => (prev === 'OCCUPANCY' ? 'ALL' : 'OCCUPANCY'));
                            }
                        }}
                        className="rounded-4 p-3 d-flex flex-column justify-content-between transition-all flex-shrink-0"
                        style={{
                            flex: '1 0 clamp(250px, 75vw, 300px)',
                            minWidth: '240px',
                            scrollSnapAlign: 'start',
                            background:
                                activeTab === 'OCCUPANCY'
                                    ? 'linear-gradient(180deg, #1f1d18 0%, #18181b 100%)'
                                    : '#18181b',
                            border:
                                activeTab === 'OCCUPANCY'
                                    ? '2px solid #f59e0b'
                                    : '1px solid rgba(255, 255, 255, 0.08)',
                            boxShadow:
                                activeTab === 'OCCUPANCY'
                                    ? '0 0 20px rgba(245, 158, 11, 0.35)'
                                    : '0 4px 12px rgba(0,0,0,0.35)',
                            cursor: 'pointer'
                        }}
                    >
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span
                                    className="text-uppercase fw-bold"
                                    style={{
                                        color: activeTab === 'OCCUPANCY' ? '#ffffff' : '#a1a1aa',
                                        fontSize: '0.72rem',
                                        letterSpacing: '0.8px'
                                    }}
                                >
                                    🛡️ Capacidad Global
                                </span>
                                <span
                                    className="badge rounded-pill"
                                    style={{
                                        background:
                                            activeTab === 'OCCUPANCY'
                                                ? '#f59e0b'
                                                : 'rgba(255, 255, 255, 0.08)',
                                        color: activeTab === 'OCCUPANCY' ? '#09090b' : '#d4d4d8',
                                        fontWeight: 800,
                                        fontSize: '0.7rem'
                                    }}
                                >
                                    {activeTab === 'OCCUPANCY' ? '✓ Activo' : `${globalOccupancyPct}% Aforo`}
                                </span>
                            </div>
                            <div
                                className="fw-black text-white mb-1"
                                style={{ fontSize: '1.85rem', fontWeight: 900 }}
                            >
                                {globalOccupancy.toLocaleString('es-MX')}{' '}
                                <span style={{ fontSize: '1rem', color: '#71717a' }}>
                                    / {globalCapacity.toLocaleString('es-MX')}
                                </span>
                            </div>
                        </div>

                        <div>
                            <div
                                className="progress rounded-pill mb-2"
                                style={{ height: '7px', background: '#09090b' }}
                            >
                                <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{
                                        width: `${Math.min(100, globalOccupancyPct)}%`,
                                        background:
                                            activeTab === 'OCCUPANCY'
                                                ? 'linear-gradient(90deg, #f59e0b 0%, #dc2626 100%)'
                                                : 'linear-gradient(90deg, #52525b 0%, #71717a 100%)'
                                    }}
                                />
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <small style={{ color: '#71717a', fontSize: '0.7rem' }}>
                                    5 Zonas monitoreadas
                                </small>
                                <small
                                    style={{
                                        color: activeTab === 'OCCUPANCY' ? '#fde68a' : '#a1a1aa',
                                        fontSize: '0.68rem',
                                        fontWeight: 700
                                    }}
                                >
                                    {activeTab === 'OCCUPANCY' ? 'Mostrando aforo ↓' : 'Filtrar semáforos →'}
                                </small>
                            </div>
                        </div>
                    </div>

                    {/* KPI 3: Venta Total POS (Tile interactivo -> COMMERCIAL) */}
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                            setActiveTab((prev) => (prev === 'COMMERCIAL' ? 'ALL' : 'COMMERCIAL'))
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                setActiveTab((prev) => (prev === 'COMMERCIAL' ? 'ALL' : 'COMMERCIAL'));
                            }
                        }}
                        className="rounded-4 p-3 d-flex flex-column justify-content-between transition-all flex-shrink-0"
                        style={{
                            flex: '1 0 clamp(250px, 75vw, 300px)',
                            minWidth: '240px',
                            scrollSnapAlign: 'start',
                            background:
                                activeTab === 'COMMERCIAL'
                                    ? 'linear-gradient(180deg, #1f1d18 0%, #18181b 100%)'
                                    : '#18181b',
                            border:
                                activeTab === 'COMMERCIAL'
                                    ? '2px solid #f59e0b'
                                    : '1px solid rgba(255, 255, 255, 0.08)',
                            boxShadow:
                                activeTab === 'COMMERCIAL'
                                    ? '0 0 20px rgba(245, 158, 11, 0.35)'
                                    : '0 4px 12px rgba(0,0,0,0.35)',
                            cursor: 'pointer'
                        }}
                    >
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span
                                    className="text-uppercase fw-bold"
                                    style={{
                                        color: activeTab === 'COMMERCIAL' ? '#ffffff' : '#a1a1aa',
                                        fontSize: '0.72rem',
                                        letterSpacing: '0.8px'
                                    }}
                                >
                                    🛍️ Venta Total POS
                                </span>
                                <span
                                    className="badge rounded-pill"
                                    style={{
                                        background:
                                            activeTab === 'COMMERCIAL'
                                                ? '#f59e0b'
                                                : 'rgba(255, 255, 255, 0.08)',
                                        color: activeTab === 'COMMERCIAL' ? '#09090b' : '#d4d4d8',
                                        fontWeight: 800,
                                        fontSize: '0.7rem'
                                    }}
                                >
                                    {activeTab === 'COMMERCIAL' ? '✓ Activo' : `${totalTransactions} Tx`}
                                </span>
                            </div>
                            <div
                                className="fw-black mb-1"
                                style={{
                                    fontSize: '1.85rem',
                                    fontWeight: 900,
                                    color: activeTab === 'COMMERCIAL' ? '#fde68a' : '#ffffff'
                                }}
                            >
                                {formatCurrencyMxn(totalRevenue)}
                            </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center pt-2 border-top border-secondary border-opacity-10">
                            <small style={{ color: '#71717a', fontSize: '0.7rem' }}>
                                Prom: <strong className="text-light">{formatCurrencyMxn(averageTicket)}</strong>
                            </small>
                            <small
                                style={{
                                    color: activeTab === 'COMMERCIAL' ? '#fde68a' : '#a1a1aa',
                                    fontSize: '0.68rem',
                                    fontWeight: 700
                                }}
                            >
                                {activeTab === 'COMMERCIAL' ? 'Mostrando ventas ↓' : 'Filtrar ventas →'}
                            </small>
                        </div>
                    </div>

                    {/* KPI 4: Souvenirs Físicos Entregados (Tile interactivo -> SPONSORS) */}
                    <div
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                            setActiveTab((prev) => (prev === 'SPONSORS' ? 'ALL' : 'SPONSORS'))
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                setActiveTab((prev) => (prev === 'SPONSORS' ? 'ALL' : 'SPONSORS'));
                            }
                        }}
                        className="rounded-4 p-3 d-flex flex-column justify-content-between transition-all flex-shrink-0"
                        style={{
                            flex: '1 0 clamp(250px, 75vw, 300px)',
                            minWidth: '240px',
                            scrollSnapAlign: 'start',
                            background:
                                activeTab === 'SPONSORS'
                                    ? 'linear-gradient(180deg, #1f1618 0%, #18181b 100%)'
                                    : '#18181b',
                            border:
                                activeTab === 'SPONSORS'
                                    ? '2px solid #dc2626'
                                    : '1px solid rgba(255, 255, 255, 0.08)',
                            boxShadow:
                                activeTab === 'SPONSORS'
                                    ? '0 0 20px rgba(220, 38, 38, 0.35)'
                                    : '0 4px 12px rgba(0,0,0,0.35)',
                            cursor: 'pointer'
                        }}
                    >
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span
                                    className="text-uppercase fw-bold"
                                    style={{
                                        color: activeTab === 'SPONSORS' ? '#ffffff' : '#a1a1aa',
                                        fontSize: '0.72rem',
                                        letterSpacing: '0.8px'
                                    }}
                                >
                                    🎁 Souvenirs Entregados
                                </span>
                                <span
                                    className="badge rounded-pill"
                                    style={{
                                        background:
                                            activeTab === 'SPONSORS'
                                                ? '#dc2626'
                                                : 'rgba(255, 255, 255, 0.08)',
                                        color: '#ffffff',
                                        fontWeight: 800,
                                        fontSize: '0.7rem'
                                    }}
                                >
                                    {activeTab === 'SPONSORS' ? '✓ Activo' : `Restan ${physicalRemaining}`}
                                </span>
                            </div>
                            <div
                                className="fw-black text-white mb-1"
                                style={{ fontSize: '1.85rem', fontWeight: 900 }}
                            >
                                {physicalRedeemed}{' '}
                                <span style={{ fontSize: '1rem', color: '#71717a' }}>
                                    / {physicalLimit}
                                </span>
                            </div>
                        </div>

                        <div>
                            <div
                                className="progress rounded-pill mb-2"
                                style={{ height: '7px', background: '#09090b' }}
                            >
                                <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{
                                        width: `${Math.min(100, stockRedeemedPct)}%`,
                                        background: activeTab === 'SPONSORS' ? '#f59e0b' : '#71717a'
                                    }}
                                />
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <small style={{ color: '#71717a', fontSize: '0.7rem' }}>
                                    Insignias: {digitalBadgesAwarded}
                                </small>
                                <small
                                    style={{
                                        color: activeTab === 'SPONSORS' ? '#fca5a5' : '#a1a1aa',
                                        fontSize: '0.68rem',
                                        fontWeight: 700
                                    }}
                                >
                                    {activeTab === 'SPONSORS' ? 'Mostrando marcas ↓' : 'Filtrar marcas →'}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* =========================================================================
                    SECCIÓN 2: SEMÁFOROS DE PROTECCIÓN CIVIL POR ZONA
                   ========================================================================= */}
                {(activeTab === 'ALL' || activeTab === 'OCCUPANCY') && (
                    <div
                        className="rounded-4 p-4 mb-4"
                        style={{
                            background: '#18181b',
                            border: '1px solid rgba(255, 255, 255, 0.12)',
                            boxShadow: '0 15px 35px rgba(0,0,0,0.6)'
                        }}
                    >
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                            <div>
                                <h5 className="fw-bold text-white mb-0" style={{ fontSize: '1.08rem' }}>
                                    <i className="bi bi-shield-fill-check text-warning me-2"></i>
                                    Semáforos de Protección Civil y Aforo por Zona (En Vivo)
                                </h5>
                                <small style={{ color: '#a1a1aa', fontSize: '0.76rem' }}>
                                    Verde (&lt;70% Aforo Normal) • Amarillo (70%-89% Precaución) • Rojo
                                    (&ge;90% Alerta Sobrecupero)
                                </small>
                            </div>
                        </div>

                        <div
                            className="d-flex overflow-x-auto pb-2 gap-3"
                            style={{
                                scrollSnapType: 'x mandatory',
                                WebkitOverflowScrolling: 'touch',
                                scrollbarWidth: 'thin'
                            }}
                        >
                            {zones.map((z) => {
                                const sem = getOccupancySemaphore(z.occupancyPercentage);
                                const isRedAlert = sem.status === 'RED';

                                return (
                                    <div
                                        key={z.zoneCode}
                                        style={{
                                            flex: '1 0 clamp(240px, 70vw, 280px)',
                                            minWidth: '240px',
                                            scrollSnapAlign: 'start'
                                        }}
                                    >
                                        <motion.div
                                            animate={
                                                isRedAlert
                                                    ? {
                                                          boxShadow: [
                                                              '0 0 0px rgba(220,38,38,0)',
                                                              '0 0 22px rgba(220,38,38,0.65)',
                                                              '0 0 0px rgba(220,38,38,0)'
                                                          ]
                                                      }
                                                    : {}
                                            }
                                            transition={
                                                isRedAlert
                                                    ? { duration: 1.6, repeat: Infinity }
                                                    : undefined
                                            }
                                            className="rounded-4 p-3 h-100 d-flex flex-column justify-content-between"
                                            style={{
                                                background: '#202024',
                                                border: isRedAlert
                                                    ? '2px solid #dc2626'
                                                    : sem.status === 'YELLOW'
                                                    ? '1.5px solid rgba(245,158,11,0.5)'
                                                    : '1px solid rgba(255,255,255,0.08)'
                                            }}
                                        >
                                            <div>
                                                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                                    <div>
                                                        <span
                                                            className="badge mb-1"
                                                            style={{
                                                                background: 'rgba(255,255,255,0.08)',
                                                                color: '#d4d4d8',
                                                                fontSize: '0.65rem',
                                                                fontFamily: 'monospace'
                                                            }}
                                                        >
                                                            ZONA: {z.zoneCode}
                                                        </span>
                                                        <h6
                                                            className="fw-bold text-white mb-0"
                                                            style={{ fontSize: '0.95rem' }}
                                                        >
                                                            {z.zoneName}
                                                        </h6>
                                                    </div>

                                                    <span
                                                        className="badge rounded-pill px-2 py-1"
                                                        style={{
                                                            background: sem.bgRgba,
                                                            border: `1px solid ${sem.hexColor}`,
                                                            color: '#ffffff',
                                                            fontSize: '0.65rem',
                                                            fontWeight: 800
                                                        }}
                                                    >
                                                        {sem.label}
                                                    </span>
                                                </div>

                                                <div className="d-flex align-items-baseline justify-content-between my-2">
                                                    <div
                                                        className="fw-black text-white"
                                                        style={{ fontSize: '1.45rem', fontWeight: 900 }}
                                                    >
                                                        {Number(z.currentOccupancy || 0).toLocaleString(
                                                            'es-MX'
                                                        )}{' '}
                                                        <span
                                                            style={{
                                                                fontSize: '0.85rem',
                                                                color: '#a1a1aa'
                                                            }}
                                                        >
                                                            /{' '}
                                                            {Number(
                                                                z.capacityLimit || 0
                                                            ).toLocaleString('es-MX')}
                                                        </span>
                                                    </div>
                                                    <span
                                                        className="fw-bold"
                                                        style={{
                                                            color: sem.hexColor,
                                                            fontSize: '0.92rem'
                                                        }}
                                                    >
                                                        {Number(z.occupancyPercentage || 0).toFixed(1)}%
                                                    </span>
                                                </div>
                                            </div>

                                            <div>
                                                <div
                                                    className="progress rounded-pill mb-2"
                                                    style={{ height: '10px', background: '#09090b' }}
                                                >
                                                    <div
                                                        className="progress-bar"
                                                        role="progressbar"
                                                        style={{
                                                            width: `${Math.min(
                                                                100,
                                                                Number(z.occupancyPercentage || 0)
                                                            )}%`,
                                                            background: sem.hexColor
                                                        }}
                                                    />
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center">
                                                    <small
                                                        style={{
                                                            color: z.allowReentry
                                                                ? '#6ee7b7'
                                                                : '#fca5a5',
                                                            fontSize: '0.68rem',
                                                            fontWeight: 700
                                                        }}
                                                    >
                                                        {z.allowReentry
                                                            ? '🔄 Reingreso Permitido'
                                                            : '🔒 Acceso Único (Sin Reingreso)'}
                                                    </small>
                                                </div>
                                            </div>
                                        </motion.div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* =========================================================================
                    SECCIÓN 3: ANALÍTICA COMERCIAL POS Y/O PODIO DE PATROCINADORES
                   ========================================================================= */}
                {(activeTab === 'ALL' || activeTab === 'COMMERCIAL' || activeTab === 'SPONSORS') && (
                    <div className="row g-4 mb-4">
                        {/* Podio de Patrocinadores (Visible en ALL y SPONSORS) */}
                        {(activeTab === 'ALL' || activeTab === 'SPONSORS') && (
                            <div className={activeTab === 'ALL' ? 'col-12 col-lg-5' : 'col-12 col-lg-7'}>
                                <div
                                    className="rounded-4 p-4 h-100"
                                    style={{
                                        background: '#18181b',
                                        border: '1px solid rgba(220, 38, 38, 0.4)'
                                    }}
                                >
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <h5
                                                className="fw-bold text-white mb-0"
                                                style={{ fontSize: '1.05rem' }}
                                            >
                                                <i className="bi bi-trophy-fill text-warning me-2"></i>
                                                Engagement de Patrocinadores
                                            </h5>
                                            <small style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>
                                                Participación en dinámicas de marca (Caliente.mx, Nissan)
                                            </small>
                                        </div>
                                        <span
                                            className="badge rounded-pill px-3 py-2"
                                            style={{
                                                background: 'rgba(220, 38, 38, 0.2)',
                                                border: '1px solid #dc2626',
                                                color: '#ffffff',
                                                fontWeight: 800
                                            }}
                                        >
                                            {totalActivations} Activaciones
                                        </span>
                                    </div>

                                    {brandRankings.length === 0 ? (
                                        <div
                                            className="p-4 rounded-3 text-center"
                                            style={{ background: '#27272a', color: '#a1a1aa' }}
                                        >
                                            Aún no se registran activaciones de marca. Usa el simulador
                                            para generar retos.
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-3">
                                            {brandRankings.map((brand, idx) => (
                                                <div
                                                    key={brand.sponsorName || idx}
                                                    className="p-3 rounded-3"
                                                    style={{
                                                        background: '#27272a',
                                                        border: '1px solid rgba(245, 158, 11, 0.3)'
                                                    }}
                                                >
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <div>
                                                            <span
                                                                className="badge me-2"
                                                                style={{
                                                                    background:
                                                                        idx === 0 ? '#f59e0b' : '#dc2626',
                                                                    color:
                                                                        idx === 0 ? '#09090b' : '#ffffff',
                                                                    fontWeight: 900
                                                                }}
                                                            >
                                                                #{idx + 1}
                                                            </span>
                                                            <strong className="text-white">
                                                                {brand.sponsorName}
                                                            </strong>
                                                            <small
                                                                className="ms-2"
                                                                style={{
                                                                    color: '#a1a1aa',
                                                                    fontSize: '0.74rem'
                                                                }}
                                                            >
                                                                ({brand.stationName})
                                                            </small>
                                                        </div>
                                                        <div className="text-end">
                                                            <strong
                                                                style={{
                                                                    color: '#fde68a',
                                                                    fontSize: '0.92rem'
                                                                }}
                                                            >
                                                                {brand.activationsCount} retos
                                                            </strong>
                                                            <small
                                                                className="ms-2"
                                                                style={{
                                                                    color: '#a1a1aa',
                                                                    fontSize: '0.75rem'
                                                                }}
                                                            >
                                                                ({Number(brand.percentage || 0).toFixed(1)}
                                                                %)
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <div
                                                        className="progress rounded-pill mt-2"
                                                        style={{ height: '10px', background: '#09090b' }}
                                                    >
                                                        <div
                                                            className="progress-bar"
                                                            role="progressbar"
                                                            style={{
                                                                width: `${Math.min(
                                                                    100,
                                                                    Number(brand.percentage || 0)
                                                                )}%`,
                                                                background:
                                                                    idx === 0
                                                                        ? 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)'
                                                                        : 'linear-gradient(90deg, #dc2626 0%, #f59e0b 100%)'
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Detalle Especial de Souvenirs & Stock Físico (visible solo en vista SPONSORS) */}
                        {activeTab === 'SPONSORS' && (
                            <div className="col-12 col-lg-5">
                                <div
                                    className="rounded-4 p-4 h-100 d-flex flex-column justify-content-between"
                                    style={{
                                        background: '#18181b',
                                        border: '1px solid rgba(220, 38, 38, 0.45)'
                                    }}
                                >
                                    <div>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h5 className="fw-bold text-white mb-0" style={{ fontSize: '1.05rem' }}>
                                                <i className="bi bi-gift-fill text-warning me-2"></i>
                                                Carpa de Premios y Souvenirs
                                            </h5>
                                            <span
                                                className="badge rounded-pill px-3 py-1"
                                                style={{
                                                    background:
                                                        physicalRemaining > 0
                                                            ? 'rgba(245, 158, 11, 0.2)'
                                                            : 'rgba(220, 38, 38, 0.25)',
                                                    border:
                                                        physicalRemaining > 0
                                                            ? '1px solid #f59e0b'
                                                            : '1px solid #dc2626',
                                                    color: physicalRemaining > 0 ? '#fde68a' : '#fca5a5',
                                                    fontWeight: 800,
                                                    fontSize: '0.72rem'
                                                }}
                                            >
                                                {physicalRemaining > 0
                                                    ? `Stock: ${physicalRemaining} disp.`
                                                    : 'Stock Físico Agotado'}
                                            </span>
                                        </div>

                                        <div className="p-3 rounded-3 mb-3" style={{ background: '#27272a' }}>
                                            <div className="d-flex justify-content-between align-items-baseline mb-1">
                                                <span style={{ color: '#a1a1aa', fontSize: '0.76rem' }}>
                                                    Canjes Físicos Entregados
                                                </span>
                                                <strong
                                                    className="text-white"
                                                    style={{ fontSize: '1.3rem', fontWeight: 900 }}
                                                >
                                                    {physicalRedeemed}{' '}
                                                    <span style={{ fontSize: '0.85rem', color: '#71717a' }}>
                                                        / {physicalLimit}
                                                    </span>
                                                </strong>
                                            </div>
                                            <div
                                                className="progress rounded-pill mb-2"
                                                style={{ height: '10px', background: '#09090b' }}
                                            >
                                                <div
                                                    className="progress-bar"
                                                    role="progressbar"
                                                    style={{
                                                        width: `${Math.min(100, stockRedeemedPct)}%`,
                                                        background: '#f59e0b'
                                                    }}
                                                />
                                            </div>
                                            <div
                                                className="d-flex justify-content-between text-secondary"
                                                style={{ fontSize: '0.72rem' }}
                                            >
                                                <span>Avance de entrega: {stockRedeemedPct.toFixed(1)}%</span>
                                                <span>Límite de inventario: {physicalLimit} pzas</span>
                                            </div>
                                        </div>

                                        <div
                                            className="p-3 rounded-3"
                                            style={{
                                                background: '#27272a',
                                                border: '1px solid rgba(220, 38, 38, 0.25)'
                                            }}
                                        >
                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                <i className="bi bi-shield-shaded text-danger fs-5"></i>
                                                <div
                                                    className="fw-bold text-white"
                                                    style={{ fontSize: '0.88rem' }}
                                                >
                                                    Insignias Digitales (Fallback Contingencia)
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    color: '#d4d4d8',
                                                    fontSize: '0.76rem',
                                                    lineHeight: 1.4
                                                }}
                                            >
                                                Al alcanzar el tope de 300 souvenirs físicos, el backend
                                                transmuta dinámicamente el cupón dorado en una{' '}
                                                <strong className="text-warning">
                                                    Insignia Leyenda Digital
                                                </strong>{' '}
                                                conmemorativa.
                                            </div>
                                            <div className="mt-2 pt-2 border-top border-secondary border-opacity-25 d-flex justify-content-between align-items-center">
                                                <span style={{ color: '#a1a1aa', fontSize: '0.72rem' }}>
                                                    Total otorgadas:
                                                </span>
                                                <span
                                                    className="badge bg-danger fw-bold"
                                                    style={{ fontSize: '0.78rem' }}
                                                >
                                                    🎖️ {digitalBadgesAwarded} Aficionados
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Ranking de Productos más vendidos en Stands POS (Visible en ALL y COMMERCIAL) */}
                        {(activeTab === 'ALL' || activeTab === 'COMMERCIAL') && (
                            <div className={activeTab === 'ALL' ? 'col-12 col-lg-7' : 'col-12'}>
                                <div
                                    className="rounded-4 p-4 h-100"
                                    style={{
                                        background: '#18181b',
                                        border: '1px solid rgba(245, 158, 11, 0.4)'
                                    }}
                                >
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <h5
                                                className="fw-bold text-white mb-0"
                                                style={{ fontSize: '1.05rem' }}
                                            >
                                                <i className="bi bi-cart-check-fill text-warning me-2"></i>
                                                Ranking de Productos y Stands POS
                                            </h5>
                                            <small style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>
                                                Desglose comercial en tiempo real (XoloShop, Tacos & Grill)
                                            </small>
                                        </div>
                                        <span
                                            className="badge rounded-pill px-3 py-2"
                                            style={{
                                                background: 'rgba(245, 158, 11, 0.18)',
                                                border: '1px solid #f59e0b',
                                                color: '#fde68a',
                                                fontWeight: 800
                                            }}
                                        >
                                            {formatCurrencyMxn(totalRevenue)}
                                        </span>
                                    </div>

                                    {topProducts.length === 0 ? (
                                        <div
                                            className="p-4 rounded-3 text-center"
                                            style={{ background: '#27272a', color: '#a1a1aa' }}
                                        >
                                            Sin ventas registradas aún. Usa el simulador para poblar
                                            estadísticas.
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-dark table-hover align-middle mb-0">
                                                <thead>
                                                    <tr
                                                        style={{
                                                            color: '#a1a1aa',
                                                            fontSize: '0.72rem',
                                                            textTransform: 'uppercase'
                                                        }}
                                                    >
                                                        <th>Producto / Concepto</th>
                                                        <th>Stand POS</th>
                                                        <th className="text-center">Unidades</th>
                                                        <th className="text-end">Ingresos</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {topProducts.map((prod, idx) => (
                                                        <tr
                                                            key={`${prod.productName}-${idx}`}
                                                            style={{ fontSize: '0.84rem' }}
                                                        >
                                                            <td className="fw-bold text-white">
                                                                {prod.productName}
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className="badge"
                                                                    style={{
                                                                        background:
                                                                            'rgba(255,255,255,0.08)',
                                                                        color: '#d4d4d8',
                                                                        fontSize: '0.7rem'
                                                                    }}
                                                                >
                                                                    {prod.stationName}
                                                                </span>
                                                            </td>
                                                            <td className="text-center fw-bold text-warning">
                                                                {prod.totalQuantity}
                                                            </td>
                                                            <td
                                                                className="text-end fw-bold"
                                                                style={{ color: '#6ee7b7' }}
                                                            >
                                                                {formatCurrencyMxn(prod.totalRevenue)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* =========================================================================
                    SECCIÓN 4: MÓDULO INSPECTOR 360° DEL AFICIONADO (CON PRIVACIDAD PII)
                   ========================================================================= */}
                {(activeTab === 'ALL' || activeTab === 'SUPPORT') && (
                    <div
                        className="rounded-4 p-4 mb-4"
                        style={{
                            background: '#18181b',
                            border: '2px solid rgba(245, 158, 11, 0.5)',
                            boxShadow: '0 20px 45px rgba(0,0,0,0.7)'
                        }}
                    >
                        <div className="row align-items-center justify-content-between g-3 mb-3">
                            <div className="col-12 col-lg-6">
                                <h5 className="fw-bold text-white mb-1" style={{ fontSize: '1.12rem' }}>
                                    <i className="bi bi-person-bounding-box text-warning me-2"></i>
                                    Inspector 360° del Aficionado (Soporte y Auditoría)
                                </h5>
                                <small style={{ color: '#a1a1aa', fontSize: '0.78rem' }}>
                                    Busca por folio (<code>XOLO-0001</code>), nombre, teléfono o correo.
                                    Los datos personales (PII) se muestran enmascarados por defecto.
                                </small>
                            </div>

                            <div className="col-12 col-lg-6">
                                <div className="input-group">
                                    <span
                                        className="input-group-text border-0"
                                        style={{ background: '#27272a', color: '#f59e0b' }}
                                    >
                                        <i className="bi bi-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Buscar por Folio (ej. XOLO-0001), Nombre, Teléfono o Correo..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        style={{
                                            background: '#09090b',
                                            border: '1px solid #3f3f46',
                                            color: '#ffffff',
                                            fontSize: '0.9rem'
                                        }}
                                    />
                                    {searchQuery && (
                                        <button
                                            type="button"
                                            onClick={() => setSearchQuery('')}
                                            className="btn btn-outline-secondary"
                                        >
                                            Limpiar
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {searching ? (
                            <div className="text-center py-4 text-warning">
                                <div className="spinner-border spinner-border-sm me-2" role="status" />
                                Buscando participantes en el padrón...
                            </div>
                        ) : searchResults.length === 0 ? (
                            <div
                                className="p-4 rounded-3 text-center"
                                style={{ background: '#27272a', color: '#a1a1aa' }}
                            >
                                No se encontraron participantes con el criterio ingresado.
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-dark table-hover align-middle mb-0">
                                    <thead>
                                        <tr
                                            style={{
                                                color: '#a1a1aa',
                                                fontSize: '0.72rem',
                                                textTransform: 'uppercase'
                                            }}
                                        >
                                            <th>Folio Boleto</th>
                                            <th>Aficionado</th>
                                            <th>Contacto (PII Enmascarado)</th>
                                            <th>Categoría</th>
                                            <th>Folio Tómbola</th>
                                            <th>Estado Asistencia</th>
                                            <th>Recompensa</th>
                                            <th className="text-end">Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {searchResults.map((item) => (
                                            <tr key={item.id || item.ticketFolio} style={{ fontSize: '0.84rem' }}>
                                                <td
                                                    className="fw-bold text-warning"
                                                    style={{ fontFamily: 'monospace' }}
                                                >
                                                    {item.ticketFolio}
                                                </td>
                                                <td className="fw-bold text-white">{item.fullName}</td>
                                                <td style={{ color: '#a1a1aa', fontSize: '0.78rem' }}>
                                                    <div>
                                                        <i className="bi bi-telephone-fill me-1 text-secondary"></i>
                                                        {item.maskedPhone}
                                                    </div>
                                                    <div>
                                                        <i className="bi bi-envelope-fill me-1 text-secondary"></i>
                                                        {item.maskedEmail}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span
                                                        className="badge"
                                                        style={{
                                                            background:
                                                                item.ticketCategory === 'VIP'
                                                                    ? '#f59e0b'
                                                                    : item.ticketCategory === 'CANCHA'
                                                                    ? '#dc2626'
                                                                    : '#3f3f46',
                                                            color:
                                                                item.ticketCategory === 'VIP'
                                                                    ? '#09090b'
                                                                    : '#ffffff',
                                                            fontWeight: 800
                                                        }}
                                                    >
                                                        {item.ticketCategory}
                                                    </span>
                                                </td>
                                                <td
                                                    style={{
                                                        fontFamily: 'monospace',
                                                        color: '#fde68a',
                                                        fontWeight: 700
                                                    }}
                                                >
                                                    #{String(item.raffleTicketCode || '').replace(/^#/, '')}
                                                </td>
                                                <td>
                                                    {item.hasAttended ? (
                                                        <span
                                                            className="badge"
                                                            style={{
                                                                background: 'rgba(16, 185, 129, 0.2)',
                                                                color: '#6ee7b7',
                                                                border: '1px solid #10b981'
                                                            }}
                                                        >
                                                            ✅ En Estadio
                                                        </span>
                                                    ) : (
                                                        <span
                                                            className="badge"
                                                            style={{
                                                                background: 'rgba(255, 255, 255, 0.08)',
                                                                color: '#a1a1aa'
                                                            }}
                                                        >
                                                            ⏳ Pre-Registro
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {item.hasRedeemedReward ? (
                                                        <span className="badge bg-success">
                                                            🎁 Canjeado
                                                        </span>
                                                    ) : item.hasEarnedReward ? (
                                                        <span className="badge bg-warning text-dark fw-bold">
                                                            🏆 Cupón Dorado
                                                        </span>
                                                    ) : item.receivedDigitalBadge ? (
                                                        <span className="badge bg-danger">
                                                            🎖️ Insignia Leyenda
                                                        </span>
                                                    ) : (
                                                        <span className="text-secondary small">
                                                            En recorrido
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-end">
                                                    <button
                                                        type="button"
                                                        disabled={loading360}
                                                        onClick={() =>
                                                            handleOpenParticipant360(item.ticketFolio)
                                                        }
                                                        className="btn btn-sm fw-bold"
                                                        style={{
                                                            background:
                                                                'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                                            color: '#09090b',
                                                            border: 'none',
                                                            fontSize: '0.76rem'
                                                        }}
                                                    >
                                                        <i className="bi bi-eye-fill me-1"></i>
                                                        Expediente 360°
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* =========================================================================
                    SECCIÓN 5: BITÁCORA EN VIVO DE ACCESOS Y EVENTOS RECIENTES (PROGRESSIVE DISCLOSURE)
                   ========================================================================= */}
                {(activeTab === 'ALL' || activeTab === 'OCCUPANCY') && recentLogs.length > 0 && (
                    <div
                        className="rounded-4 p-3 mb-4"
                        style={{
                            background: '#18181b',
                            border: '1px solid rgba(255,255,255,0.08)'
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h6 className="fw-bold text-white mb-0" style={{ fontSize: '0.95rem' }}>
                                    <i className="bi bi-clock-history text-secondary me-2"></i>
                                    Bitácora de Operaciones en Vivo ({recentLogs.length})
                                </h6>
                                <small style={{ color: '#71717a', fontSize: '0.75rem' }}>
                                    Información secundaria de auditoría perimetral y validaciones recientes
                                </small>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm rounded-pill px-3 py-1 text-white"
                                onClick={() => setShowAuditLogs((prev) => !prev)}
                                style={{
                                    background: showAuditLogs ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.05)',
                                    border: '1px solid rgba(255, 255, 255, 0.12)',
                                    fontSize: '0.78rem',
                                    fontWeight: 700
                                }}
                            >
                                {showAuditLogs ? '▲ Ocultar Bitácora' : `▼ Ver Bitácora (${recentLogs.length})`}
                            </button>
                        </div>

                        <AnimatePresence>
                            {showAuditLogs && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden mt-3 pt-3 border-top border-secondary border-opacity-10"
                                >
                                    <div
                                        className="d-flex overflow-x-auto pb-2 gap-2"
                                        style={{
                                            scrollSnapType: 'x mandatory',
                                            WebkitOverflowScrolling: 'touch',
                                            scrollbarWidth: 'thin'
                                        }}
                                    >
                                        {recentLogs.slice(0, 10).map((log) => (
                                            <div
                                                key={log.id}
                                                style={{
                                                    flex: '0 0 clamp(240px, 60vw, 290px)',
                                                    minWidth: '240px',
                                                    scrollSnapAlign: 'start'
                                                }}
                                            >
                                                <div
                                                    className="p-2 px-3 rounded-3 d-flex justify-content-between align-items-center h-100"
                                                    style={{
                                                        background: '#202024',
                                                        border: '1px solid rgba(255, 255, 255, 0.06)',
                                                        borderLeft: '3px solid #71717a'
                                                    }}
                                                >
                                                    <div className="text-truncate me-2">
                                                        <div
                                                            className="fw-bold text-white text-truncate"
                                                            style={{ fontSize: '0.8rem' }}
                                                        >
                                                            {log.ticketFolio} • {log.participantName}
                                                        </div>
                                                        <small style={{ color: '#a1a1aa', fontSize: '0.72rem' }} className="text-truncate d-block">
                                                            {log.stationName}: {log.actionName}
                                                        </small>
                                                    </div>
                                                    <span
                                                        className="badge flex-shrink-0"
                                                        style={{
                                                            background: 'rgba(255,255,255,0.06)',
                                                            color: '#d4d4d8',
                                                            fontSize: '0.68rem',
                                                            fontFamily: 'monospace'
                                                        }}
                                                    >
                                                        {formatDateTimeShort(log.timestampUtc)}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            {/* =========================================================================
                MODAL COMPLETO: EXPEDIENTE 360° DEL AFICIONADO (CON TOGGLE PII Y TIMELINE)
               ========================================================================= */}
            <AnimatePresence>
                {selectedParticipant360 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
                        style={{
                            background: 'rgba(0, 0, 0, 0.85)',
                            backdropFilter: 'blur(8px)',
                            zIndex: 1060,
                            overflowY: 'auto'
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0.94, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.94, opacity: 0 }}
                            className="rounded-4 p-4 w-100 my-auto"
                            style={{
                                maxWidth: '780px',
                                maxHeight: '90vh',
                                overflowY: 'auto',
                                background: '#18181b',
                                border: '2px solid #f59e0b',
                                boxShadow: '0 25px 60px rgba(0,0,0,0.9)'
                            }}
                        >
                            {/* Encabezado del Modal 360° */}
                            <div className="d-flex justify-content-between align-items-start gap-2 pb-3 mb-3 border-bottom border-secondary border-opacity-25">
                                <div>
                                    <span
                                        className="badge rounded-pill px-3 py-1 mb-1"
                                        style={{
                                            background: 'rgba(245, 158, 11, 0.2)',
                                            border: '1px solid #f59e0b',
                                            color: '#fde68a',
                                            fontSize: '0.7rem',
                                            fontWeight: 800
                                        }}
                                    >
                                        🔍 EXPEDIENTE 360° DEL AFICIONADO
                                    </span>
                                    <h4 className="fw-bold text-white mb-0">
                                        {selectedParticipant360.fullName}
                                    </h4>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => setSelectedParticipant360(null)}
                                    className="btn btn-sm text-white border-0"
                                >
                                    <i className="bi bi-x-lg fs-5"></i>
                                </button>
                            </div>

                            {/* Cinta de Folios y Estado */}
                            <div className="row g-2 mb-3">
                                <div className="col-6 col-md-3">
                                    <div
                                        className="p-2 px-3 rounded-3"
                                        style={{ background: '#27272a' }}
                                    >
                                        <small
                                            className="d-block text-uppercase"
                                            style={{ color: '#a1a1aa', fontSize: '0.66rem' }}
                                        >
                                            Folio Boleto
                                        </small>
                                        <strong
                                            className="text-warning"
                                            style={{ fontFamily: 'monospace', fontSize: '1rem' }}
                                        >
                                            {selectedParticipant360.ticketFolio}
                                        </strong>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div
                                        className="p-2 px-3 rounded-3"
                                        style={{ background: '#27272a' }}
                                    >
                                        <small
                                            className="d-block text-uppercase"
                                            style={{ color: '#a1a1aa', fontSize: '0.66rem' }}
                                        >
                                            Categoría
                                        </small>
                                        <strong className="text-white">
                                            {selectedParticipant360.ticketCategory}
                                        </strong>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div
                                        className="p-2 px-3 rounded-3"
                                        style={{ background: '#27272a' }}
                                    >
                                        <small
                                            className="d-block text-uppercase"
                                            style={{ color: '#a1a1aa', fontSize: '0.66rem' }}
                                        >
                                            Folio Tómbola Física
                                        </small>
                                        <strong
                                            style={{
                                                color: '#fde68a',
                                                fontFamily: 'monospace',
                                                fontSize: '0.96rem'
                                            }}
                                        >
                                            #
                                            {String(
                                                selectedParticipant360.raffleTicketCode || ''
                                            ).replace(/^#/, '')}
                                        </strong>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div
                                        className="p-2 px-3 rounded-3"
                                        style={{ background: '#27272a' }}
                                    >
                                        <small
                                            className="d-block text-uppercase"
                                            style={{ color: '#a1a1aa', fontSize: '0.66rem' }}
                                        >
                                            Check-In Estadio
                                        </small>
                                        <strong
                                            style={{
                                                color: selectedParticipant360.hasAttended
                                                    ? '#6ee7b7'
                                                    : '#fca5a5',
                                                fontSize: '0.82rem'
                                            }}
                                        >
                                            {selectedParticipant360.hasAttended
                                                ? formatDateTimeShort(
                                                      selectedParticipant360.firstAccessAtUtc
                                                  )
                                                : 'Pendiente'}
                                        </strong>
                                    </div>
                                </div>
                            </div>

                            {/* Tarjeta de Protección de Datos Sensibles (PII) con Conmutador con Candado */}
                            <div
                                className="p-3 rounded-3 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2"
                                style={{
                                    background: revealPii
                                        ? 'rgba(220, 38, 38, 0.14)'
                                        : 'rgba(255, 255, 255, 0.04)',
                                    border: revealPii
                                        ? '1px solid #dc2626'
                                        : '1px solid rgba(255, 255, 255, 0.14)'
                                }}
                            >
                                <div>
                                    <div
                                        className="fw-bold text-uppercase mb-1"
                                        style={{
                                            fontSize: '0.7rem',
                                            color: revealPii ? '#fca5a5' : '#a1a1aa'
                                        }}
                                    >
                                        <i
                                            className={`bi ${
                                                revealPii ? 'bi-unlock-fill' : 'bi-lock-fill'
                                            } me-1`}
                                        ></i>
                                        Datos de Contacto (Protección PII)
                                    </div>
                                    <div className="d-flex flex-wrap gap-3" style={{ fontSize: '0.88rem' }}>
                                        <span>
                                            <strong className="text-secondary me-1">Tel:</strong>
                                            <code className="text-white">
                                                {revealPii
                                                    ? selectedParticipant360.rawPhone ||
                                                      selectedParticipant360.maskedPhone
                                                    : selectedParticipant360.maskedPhone}
                                            </code>
                                        </span>
                                        <span>
                                            <strong className="text-secondary me-1">Email:</strong>
                                            <code className="text-white">
                                                {revealPii
                                                    ? selectedParticipant360.rawEmail ||
                                                      selectedParticipant360.maskedEmail
                                                    : selectedParticipant360.maskedEmail}
                                            </code>
                                        </span>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => setRevealPii((prev) => !prev)}
                                    className="btn btn-sm fw-bold"
                                    style={{
                                        background: revealPii
                                            ? '#dc2626'
                                            : 'rgba(245, 158, 11, 0.18)',
                                        border: '1px solid #f59e0b',
                                        color: revealPii ? '#ffffff' : '#fde68a',
                                        fontSize: '0.76rem'
                                    }}
                                >
                                    {revealPii
                                        ? '🔒 Ocultar Datos Sensibles (PII)'
                                        : '👁️ Revelar Datos Sensibles (PII)'}
                                </button>
                            </div>

                            {/* Resumen de Consumos, Activaciones y Recompensas */}
                            <div className="row g-2 mb-4 text-center">
                                <div className="col-4">
                                    <div
                                        className="p-2 rounded-3"
                                        style={{ background: '#27272a' }}
                                    >
                                        <div
                                            className="fw-black text-warning"
                                            style={{ fontSize: '1.2rem', fontWeight: 900 }}
                                        >
                                            {formatCurrencyMxn(
                                                selectedParticipant360.totalSpentAmount
                                            )}
                                        </div>
                                        <small style={{ color: '#a1a1aa', fontSize: '0.7rem' }}>
                                            Gasto Total POS (
                                            {selectedParticipant360.totalPurchasesCount || 0}{' '}
                                            compras)
                                        </small>
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div
                                        className="p-2 rounded-3"
                                        style={{ background: '#27272a' }}
                                    >
                                        <div
                                            className="fw-black text-white"
                                            style={{ fontSize: '1.2rem', fontWeight: 900 }}
                                        >
                                            {selectedParticipant360.completedActivationsCount || 0}
                                        </div>
                                        <small style={{ color: '#a1a1aa', fontSize: '0.7rem' }}>
                                            Activaciones de Marca
                                        </small>
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div
                                        className="p-2 rounded-3"
                                        style={{ background: '#27272a' }}
                                    >
                                        <div
                                            className="fw-bold"
                                            style={{
                                                fontSize: '0.92rem',
                                                color: selectedParticipant360.hasRedeemedReward
                                                    ? '#6ee7b7'
                                                    : '#fde68a'
                                            }}
                                        >
                                            {selectedParticipant360.hasRedeemedReward
                                                ? '🎁 Souvenir Entregado'
                                                : selectedParticipant360.hasEarnedReward
                                                ? `🏆 ${selectedParticipant360.rewardCouponCode}`
                                                : selectedParticipant360.receivedDigitalBadge
                                                ? '🎖️ Insignia Digital'
                                                : 'En progreso'}
                                        </div>
                                        <small style={{ color: '#a1a1aa', fontSize: '0.7rem' }}>
                                            Estado de Recompensa
                                        </small>
                                    </div>
                                </div>
                            </div>

                            {/* Timeline Cronológico Inmutable de XfJourneyLogs */}
                            <div>
                                <h6 className="fw-bold text-white mb-3">
                                    <i className="bi bi-clock-history text-warning me-2"></i>
                                    Timeline Cronológico Inmutable (
                                    {Array.isArray(selectedParticipant360.timeline)
                                        ? selectedParticipant360.timeline.length
                                        : 0}{' '}
                                    eventos)
                                </h6>

                                {!Array.isArray(selectedParticipant360.timeline) ||
                                selectedParticipant360.timeline.length === 0 ? (
                                    <div
                                        className="p-3 rounded-3 text-center"
                                        style={{ background: '#27272a', color: '#a1a1aa' }}
                                    >
                                        Este participante aún no registra escaneos en estaciones del
                                        festival.
                                    </div>
                                ) : (
                                    <div className="d-flex flex-column gap-2">
                                        {selectedParticipant360.timeline.map((ev, idx) => {
                                            const iconCfg = getTimelineIconConfig(
                                                ev.stationType,
                                                ev.actionName
                                            );
                                            return (
                                                <div
                                                    key={ev.id || idx}
                                                    className="p-3 rounded-3 d-flex align-items-start justify-content-between gap-3"
                                                    style={{
                                                        background: '#27272a',
                                                        borderLeft: `4px solid ${iconCfg.color}`
                                                    }}
                                                >
                                                    <div className="d-flex align-items-start gap-3">
                                                        <div
                                                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                            style={{
                                                                width: '38px',
                                                                height: '38px',
                                                                background: iconCfg.bg,
                                                                color: iconCfg.color
                                                            }}
                                                        >
                                                            <i className={`bi ${iconCfg.icon}`}></i>
                                                        </div>
                                                        <div>
                                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                <span
                                                                    className="badge"
                                                                    style={{
                                                                        background: iconCfg.bg,
                                                                        color: iconCfg.color,
                                                                        fontSize: '0.64rem',
                                                                        fontWeight: 800
                                                                    }}
                                                                >
                                                                    {iconCfg.badge}
                                                                </span>
                                                                {ev.zoneCode && (
                                                                    <span
                                                                        className="badge"
                                                                        style={{
                                                                            background:
                                                                                'rgba(255,255,255,0.08)',
                                                                            color: '#d4d4d8',
                                                                            fontSize: '0.64rem'
                                                                        }}
                                                                    >
                                                                        Zona: {ev.zoneCode}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div
                                                                className="fw-bold text-white mt-1"
                                                                style={{ fontSize: '0.88rem' }}
                                                            >
                                                                {ev.stationName} — {ev.actionName}
                                                            </div>
                                                            <small
                                                                style={{
                                                                    color: '#a1a1aa',
                                                                    fontSize: '0.73rem'
                                                                }}
                                                            >
                                                                Operador responsable:{' '}
                                                                <strong className="text-light">
                                                                    {ev.operatorUsername || 'system'}
                                                                </strong>
                                                            </small>
                                                        </div>
                                                    </div>

                                                    <div
                                                        className="text-end flex-shrink-0"
                                                        style={{
                                                            color: '#fde68a',
                                                            fontSize: '0.72rem',
                                                            fontFamily: 'monospace'
                                                        }}
                                                    >
                                                        {formatDateTimeShort(ev.timestampUtc)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Botón flotante para abrir Simulador Demo (HU-06) */}
            <div
                style={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    zIndex: 1040
                }}
            >
                <button
                    type="button"
                    onClick={() => setDemoDrawerOpen(true)}
                    className="btn rounded-pill shadow-lg d-flex align-items-center gap-2 px-3 py-2"
                    style={{
                        background: 'linear-gradient(135deg, #18181b 0%, #121216 100%)',
                        border: '1.5px solid #f59e0b',
                        color: '#fde68a',
                        fontSize: '0.82rem',
                        fontWeight: 800,
                        boxShadow: '0 10px 28px rgba(0, 0, 0, 0.8)'
                    }}
                >
                    <span>🛠️ Simulador Demo</span>
                    <span
                        className="badge rounded-pill bg-warning text-dark px-2 py-1"
                        style={{ fontSize: '0.66rem', fontWeight: 900 }}
                    >
                        HU-06
                    </span>
                </button>
            </div>

            {/* CAJÓN LATERAL: SIMULADOR DEMO CENTRO DE MANDO (HU-06) */}
            <BoardDemoDrawer
                isOpen={demoDrawerOpen}
                onClose={() => setDemoDrawerOpen(false)}
                simulating={simulating}
                loadingMetrics={loadingMetrics}
                onSimulateAccessBurst={() =>
                    handleSimulateBurst(
                        { accessScansCount: 5, purchasesCount: 0, activationsCount: 0 },
                        'Ráfaga de Accesos (+5 asistentes)'
                    )
                }
                onSimulatePosBurst={() =>
                    handleSimulateBurst(
                        { accessScansCount: 0, purchasesCount: 5, activationsCount: 0 },
                        'Ventas POS Simuladas (+$2,500 en stands)'
                    )
                }
                onSimulateActivationBurst={() =>
                    handleSimulateBurst(
                        { accessScansCount: 0, purchasesCount: 0, activationsCount: 3 },
                        'Activaciones Simuladas (+3 retos Caliente.mx / Nissan)'
                    )
                }
                onRefreshMetrics={() => fetchLiveMetrics(false)}
                onResetDemoBoard={handleResetDemoBoard}
            />

            {/* MODAL DE SEGURIDAD PARA REINICIAR / BORRAR INFORMACIÓN DE XOLO FEST */}
            <AnimatePresence>
                {isResetModalOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={handleCloseResetModal}
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                                zIndex: 110000,
                                backdropFilter: 'blur(5px)'
                            }}
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.92, y: 15 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.92, y: 15 }}
                            transition={{ duration: 0.2 }}
                            style={{
                                position: 'fixed',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '92%',
                                maxWidth: '460px',
                                zIndex: 110001
                            }}
                        >
                            <div className="card shadow-lg border-0 rounded-4 overflow-hidden text-dark bg-white">
                                <div className="bg-danger p-3 px-4 d-flex justify-content-between align-items-center text-white">
                                    <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                        <i className="bi bi-shield-lock-fill"></i>
                                        <span>Clave de Autorización</span>
                                    </h5>
                                    <button
                                        type="button"
                                        disabled={resettingData}
                                        className="btn-close btn-close-white"
                                        onClick={handleCloseResetModal}
                                    />
                                </div>

                                <form onSubmit={handleConfirmResetSubmit}>
                                    <div className="card-body p-4">
                                        <div className="alert alert-danger py-2 px-3 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '0.82rem' }}>
                                            <i className="bi bi-exclamation-triangle-fill fs-5 flex-shrink-0"></i>
                                            <span>
                                                <strong>¡Atención!</strong> Esta acción borrará de forma permanente los participantes registrados, compras POS, activaciones de patrocinadores y aforos de Xolo Fest para iniciar una prueba desde cero.
                                            </span>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label small fw-semibold text-dark">
                                                Contraseña de seguridad:
                                            </label>
                                            <input
                                                type="password"
                                                className={`form-control ${resetPasswordError ? 'is-invalid' : ''}`}
                                                placeholder="Ingresa la contraseña..."
                                                value={resetPasswordInput}
                                                onChange={(e) => {
                                                    setResetPasswordInput(e.target.value);
                                                    if (resetPasswordError) setResetPasswordError('');
                                                }}
                                                disabled={resettingData}
                                                autoFocus
                                            />
                                            {resetPasswordError && (
                                                <div className="invalid-feedback d-block mt-2 fw-semibold">
                                                    {resetPasswordError}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="card-footer bg-light p-3 px-4 d-flex justify-content-end gap-2 border-top">
                                        <button
                                            type="button"
                                            disabled={resettingData}
                                            className="btn btn-outline-secondary px-3"
                                            onClick={handleCloseResetModal}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={resettingData}
                                            className="btn btn-danger px-4 fw-bold text-white d-flex align-items-center gap-2"
                                        >
                                            {resettingData ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                                    <span>Reiniciando...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-key-fill"></i>
                                                    <span>Validar y Reiniciar</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default XoloFestBoard;
